import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Building2, ExternalLink, Globe } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import FreshnessChip from '../components/FreshnessChip';
import { ProviderLockLink } from '../components/ProviderIdentity';
import { api } from '../lib/api';
import { DATA_SOURCE_LABELS } from '../lib/utils';
import useSeoForPage from '../hooks/useSeoForPage';

export default function ProvidersPage() {
  useSeoForPage('/anbieter');
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProviders();
  }, []);

  const loadProviders = async () => {
    try {
      const data = await api.getProviders(true);
      setProviders(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 md:py-10">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
          <Link to="/" className="hover:text-slate-900">Startseite</Link>
          <span>/</span>
          <span className="text-slate-900 font-medium">Anbieter</span>
        </div>

        <h1 className="text-3xl font-semibold text-slate-900 mb-2" style={{fontFamily: 'Space Grotesk'}}>Anbieter</h1>
        <p className="text-muted-foreground mb-8">Alle Domain-Registrare und Hosting-Anbieter im Vergleich</p>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600" />
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="provider-list">
            {providers.map(provider => {
              const isPro = provider.tier === 'pro';
              return (
                <Card key={provider.id} className="rounded-2xl hover:shadow-md transition-shadow duration-200">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {isPro && provider.logo_url ? (
                          <img src={provider.logo_url} alt={`${provider.name} Logo`} className="w-10 h-10 rounded-xl object-contain bg-white" loading="lazy" />
                        ) : (
                          <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-slate-500" />
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-slate-900">{provider.name}</h3>
                            {isPro && (
                              <Badge variant="outline" className="text-[10px] bg-sky-50 text-sky-700 border-sky-200" data-testid="pro-badge">Pro</Badge>
                            )}
                          </div>
                          <FreshnessChip lastUpdate={provider.last_import_at || provider.last_crawled_at} dataSource={provider.data_source} />
                        </div>
                      </div>
                    </div>
                    {isPro && provider.description && (
                      <p className="text-sm text-muted-foreground mb-3">{provider.description}</p>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">{DATA_SOURCE_LABELS[provider.data_source]}</span>
                      {provider.website && (
                        <a href={provider.website} target="_blank" rel="nofollow noopener noreferrer" className="text-xs text-sky-600 hover:text-sky-700 flex items-center gap-1">
                          Website <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                    {!isPro && (
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <ProviderLockLink />
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
